import React from 'react';
import { Prompt } from 'react-router-dom';
import PropTypes from 'prop-types';
import Stepper from '@material-ui/core/Stepper';
import styled from 'styled-components';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import StatusButton from '../../components/molecule/StatusIndicators/StatusButton';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
`

const Scrollable = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1 0 auto;
  height: 1px;
`

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin: 15px 15px 30px;
`

const ButtonStyled = styled(Button)`
  margin-left: 10px;
`

const StatusButtonStyled = styled(StatusButton)`
  position: absolute;
  right: 0;
  margin: 17px;
`

const StepperStyled = styled(Stepper)`
  position: relative;
  background: transparent;
  margin: 0 200px;
  flex: 1;
`
const PrimaryButtons = styled.div`
  margin-left: auto;
`
const SecondaryButtons = styled.div`
  margin-right: auto;
`
const TopContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const HorizontalLinearStepper = ({
  onFinish,
  onCancel,
  onStep,
  clean,
  form,
  disableFinish,
  stepComponents,
  wizardInfo,
  extraButtons,
  saveText,
  classes,
  statusHeader,
  statusSubheader,
  ...other
}) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set());
  const [saving, setSave] = React.useState(false);

  const getSteps = () => {
    if(stepComponents != null) {
      return stepComponents.map(step => {
        return step.label;
      });
    }
    return ['Unkown step'];
  }

  const getStepContent = step => {
    if(step != null) {
      return stepComponents[step].component;
    }
    return 'Unkown step';
  }

  const isStepOptional = step => {
    if(step != null) {
      return stepComponents[step].optional;
    }
    return false;
  }

  const isStepSkipped = step => {
    return skipped.has(step);
  }

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }
    if(onStep){
      onStep();
    }
    setActiveStep(prevActiveStep => prevActiveStep + 1);
    setSkipped(newSkipped);
  }

  const handleBack = () => {
    if(onStep){
      onStep();
    }
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  }

  const handleFinish = () => {
    setSave(true);
    onFinish();
  }

  const handleCancel = () => {
    onCancel();
  }

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      // You probably want to guard against something like this,
      // it should never occur unless someone's actively trying to break something.
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep(prevActiveStep => prevActiveStep + 1);
    setSkipped(prevSkipped => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  }

  const steps = getSteps();

  return (
    <Container {...other}>
      <TopContainer>
        {(stepComponents && stepComponents.length > 1) &&
          <StepperStyled activeStep={activeStep}>
            {steps.map((label, index) => {
              const stepProps = {};
              const labelProps = {};
              if (isStepOptional(index)) {
                labelProps.optional = <Typography variant="caption">Optional</Typography>;
              }
              if (isStepSkipped(index)) {
                stepProps.completed = false;
              }
              return (
                <Step key={label} {...stepProps}>
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </Step>
              );
            })}
          </StepperStyled>
        }
        {form && <StatusButtonStyled value={form}/>}
      </TopContainer>
      <Scrollable>
        {getStepContent(activeStep)}
      </Scrollable>
      <ButtonContainer>
        {
          extraButtons &&
          <SecondaryButtons>{extraButtons}</SecondaryButtons>
        }
        <PrimaryButtons>
          {(stepComponents && stepComponents.length > 1) &&
            <ButtonStyled
              variant="contained"
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </ButtonStyled>
          }
          {isStepOptional(activeStep) && (
            <ButtonStyled
              variant="contained"
              color="primary"
              onClick={handleSkip}
            >
              Skip
            </ButtonStyled>
          )}
          {(stepComponents && stepComponents.length > 1) &&
            <ButtonStyled
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={activeStep === steps.length - 1}
            >
              Next
            </ButtonStyled>
          }
          {
            form?.is_dc ?
              <ButtonStyled
                variant="contained"
                color="primary"
                onClick={handleFinish}
                disabled={form.primaryStatus == "Invalid"}
              >
                {saveText}
              </ButtonStyled>
              :
              <ButtonStyled
                variant="contained"
                color="primary"
                onClick={handleFinish}
                disabled={disableFinish}
              >
                {saveText}
              </ButtonStyled>
          }
          <ButtonStyled
            variant="contained"
            color="primary"
            onClick={handleCancel}
          >
            Cancel
          </ButtonStyled>
        </PrimaryButtons>
      </ButtonContainer>
      <Prompt when={!clean && !saving} message={`You have unsaved changes. Are you sure you want to leave?`}/>
    </Container>
  );
}

HorizontalLinearStepper.propTypes = {
  onFinish: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onStep: PropTypes.func,
  extraButtons: PropTypes.object,
  wizardInfo: PropTypes.object,
  stepComponents: PropTypes.array.isRequired,
  saveText: PropTypes.string,
  form: PropTypes.object,
  statusHeader: PropTypes.string,
  statusSubheader: PropTypes.string,
}

HorizontalLinearStepper.defaultProps = {
  saveText: 'Save',
}

export default HorizontalLinearStepper;
